import {
  faChevronLeft,
  faChevronRight,
  faChevronsLeft,
  faChevronsRight,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { addYears, format, setMonth, setYear } from "date-fns"
import { de } from "date-fns/locale/de"
import { useState } from "react"
import type { ComponentProps } from "react"
import { DayPicker, useNavigation } from "react-day-picker"
import { cn } from "cn"

import { Button } from "../button/button"

/**
 * Figma-Component-Set „Date Picker" (node 9796:71965) definiert 3 Grid-Varianten
 * (Tag/Monat/Jahr). `variant` legt die feinste erreichbare Ebene fest — bei `day`
 * (Standard) dienen Monat-/Jahresgrid nur der Navigation zum Tages-Grid; bei
 * `month`/`year` ist die Komponente ein eigenständiger Monats- bzw. Jahres-Picker
 * und die Auswahl kaskadiert nicht weiter nach unten. Der Nutzer wechselt per
 * Klick auf den Header (Monat/Jahr-Label) zwischen den Ebenen, ähnlich shadcn
 * Calendar `captionLayout="dropdown"`, hier aber als eigenes Grid statt
 * Native-Select, weil Figma dafür ein Tastenfeld vorsieht.
 */
type CalendarView = "day" | "month" | "year"

const YEARS_PER_PAGE = 12
const DEFAULT_TODAY_LABEL = "Heute"

/** Figma: Jahres-Grid beginnt am Anfang des Jahrzehnts (2026 → „2020 – 2031"). */
function yearRangeStartFor(date: Date) {
  return date.getFullYear() - (date.getFullYear() % 10)
}

// Figma-Monatsabkürzungen weichen von date-fns' "MMM" ab (z. B. "Jun"/"Jul" statt "Juni"/"Juli", ohne Punkt).
const MONTH_LABELS = [
  "Jan",
  "Feb",
  "März",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
]

export type CalendarProps = ComponentProps<typeof DayPicker> & {
  /** Figma: „Heute"-Button unterhalb des Grids (in allen Ansichten). */
  showTodayButton?: boolean
  /** Beschriftung des „Heute"-Buttons. */
  todayLabel?: string
  /**
   * Figma-Achse „Variante" (`day`/`month`/`year`) — legt zugleich die feinste erreichbare
   * Ebene fest. `month`/`year` sind eigenständige Monats-/Jahres-Picker: Jahr- bzw.
   * Monatsgrid dienen dort nur der Navigation, die Auswahl bleibt auf dieser Ebene stehen
   * statt weiter zum Tages-Grid zu springen.
   */
  variant?: CalendarView
  /**
   * Figma „Date Picker Header": `false` = Variante „change month or year" (nur ‹ ›),
   * `true` = „change month and year" — in der Tagesansicht zusätzlich « » zum
   * jahresweisen Blättern.
   */
  showYearNavigation?: boolean
  /** Nur bei `variant="month"`: feuert, wenn der Nutzer einen Monat auswählt. */
  onMonthSelect?: (month: Date) => void
  /** Nur bei `variant="year"`: feuert, wenn der Nutzer ein Jahr auswählt. */
  onYearSelect?: (year: number) => void
}

function NavButton({
  label,
  disabled,
  onClick,
  icon,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  icon: typeof faChevronLeft
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="medium"
      iconOnly
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} />
    </Button>
  )
}

function CalendarHeader({
  label,
  onLabelClick,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  onPrevYear,
  onNextYear,
}: {
  label: string
  onLabelClick: () => void
  onPrev: () => void
  onNext: () => void
  prevDisabled?: boolean
  nextDisabled?: boolean
  /** Gesetzt → Figma-Variante „change month and year" mit zusätzlichen « »-Buttons außen. */
  onPrevYear?: () => void
  onNextYear?: () => void
}) {
  return (
    <div className="flex w-full items-center justify-between">
      {onPrevYear && (
        <NavButton
          label="Ein Jahr zurück"
          icon={faChevronsLeft}
          onClick={onPrevYear}
        />
      )}
      <NavButton
        label="Zurück"
        icon={faChevronLeft}
        disabled={prevDisabled}
        onClick={onPrev}
      />
      <button
        type="button"
        onClick={onLabelClick}
        className="cursor-pointer text-sm font-semibold text-labels-primary"
      >
        {label}
      </button>
      <NavButton
        label="Weiter"
        icon={faChevronRight}
        disabled={nextDisabled}
        onClick={onNext}
      />
      {onNextYear && (
        <NavButton
          label="Ein Jahr weiter"
          icon={faChevronsRight}
          onClick={onNextYear}
        />
      )}
    </div>
  )
}

/** Ersetzt die eingebaute `Caption` von `react-day-picker`, damit das Monat/Jahr-Label anklickbar ist. */
function DayCaption({
  onLabelClick,
  showYearNavigation,
}: {
  onLabelClick: () => void
  showYearNavigation?: boolean
}) {
  const { goToMonth, nextMonth, previousMonth, currentMonth } = useNavigation()

  return (
    <CalendarHeader
      label={format(currentMonth, "MMMM yyyy", { locale: de })}
      onLabelClick={onLabelClick}
      onPrev={() => previousMonth && goToMonth(previousMonth)}
      onNext={() => nextMonth && goToMonth(nextMonth)}
      prevDisabled={!previousMonth}
      nextDisabled={!nextMonth}
      onPrevYear={
        showYearNavigation
          ? () => goToMonth(addYears(currentMonth, -1))
          : undefined
      }
      onNextYear={
        showYearNavigation
          ? () => goToMonth(addYears(currentMonth, 1))
          : undefined
      }
    />
  )
}

function GridSlot({
  selected,
  current,
  onClick,
  children,
}: {
  selected?: boolean
  current?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-9 flex-1 items-center justify-center rounded-full text-sm text-brand-blue-100 hover:bg-neutrals-v2-200",
        current && "bg-neutrals-v2-300",
        selected &&
          "bg-brand-red-200! font-semibold text-neutrals-v2-100! hover:bg-brand-red-200!"
      )}
    >
      {children}
    </button>
  )
}

function MonthGrid({
  year,
  selectedMonth,
  onSelect,
  onHeaderClick,
}: {
  year: number
  selectedMonth: Date
  onSelect: (month: Date) => void
  onHeaderClick: () => void
}) {
  const now = new Date()

  return (
    <div className="flex w-68 flex-col gap-1">
      <CalendarHeader
        label={String(year)}
        onLabelClick={onHeaderClick}
        onPrev={() => onSelect(setYear(selectedMonth, year - 1))}
        onNext={() => onSelect(setYear(selectedMonth, year + 1))}
      />
      <div className="grid grid-cols-3 gap-1">
        {MONTH_LABELS.map((label, index) => (
          <GridSlot
            key={label}
            current={year === now.getFullYear() && index === now.getMonth()}
            selected={
              year === selectedMonth.getFullYear() &&
              index === selectedMonth.getMonth()
            }
            onClick={() =>
              onSelect(setMonth(setYear(selectedMonth, year), index))
            }
          >
            {label}
          </GridSlot>
        ))}
      </div>
    </div>
  )
}

function YearGrid({
  rangeStart,
  selectedYear,
  onSelect,
  onNavigate,
}: {
  rangeStart: number
  selectedYear: number
  onSelect: (year: number) => void
  onNavigate: (rangeStart: number) => void
}) {
  const now = new Date()
  const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => rangeStart + i)

  return (
    <div className="flex w-68 flex-col gap-1">
      <CalendarHeader
        label={`${rangeStart} – ${rangeStart + YEARS_PER_PAGE - 1}`}
        onLabelClick={() => onNavigate(rangeStart)}
        onPrev={() => onNavigate(rangeStart - YEARS_PER_PAGE)}
        onNext={() => onNavigate(rangeStart + YEARS_PER_PAGE)}
      />
      <div className="grid grid-cols-3 gap-1">
        {years.map((year) => (
          <GridSlot
            key={year}
            current={year === now.getFullYear()}
            selected={year === selectedYear}
            onClick={() => onSelect(year)}
          >
            {year}
          </GridSlot>
        ))}
      </div>
    </div>
  )
}

// oxlint-disable-next-line complexity max-lines-per-function
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  showTodayButton = true,
  todayLabel = DEFAULT_TODAY_LABEL,
  variant = "day",
  onMonthSelect,
  onYearSelect,
  showYearNavigation = false,
  month,
  onMonthChange,
  ...props
}: CalendarProps) {
  const [view, setView] = useState<CalendarView>(variant)
  const [uncontrolledMonth, setUncontrolledMonth] = useState(
    () => month ?? new Date()
  )
  const [yearRangeStart, setYearRangeStart] = useState(() =>
    yearRangeStartFor(uncontrolledMonth)
  )
  const displayedMonth = month ?? uncontrolledMonth

  const navigateToMonth = (next: Date) => {
    setUncontrolledMonth(next)
    onMonthChange?.(next)
  }

  return (
    <div
      className={cn(
        "flex w-fit min-w-33 flex-col items-center gap-2 rounded-xl border border-neutrals-v2-200 bg-backgrounds-primary p-4 drop-shadow-[0px_8px_10px_-6px_rgba(62,58,50,0.1),0px_2px_25px_-5px_rgba(62,58,50,0.1)]",
        className
      )}
    >
      {view === "month" && (
        <MonthGrid
          year={displayedMonth.getFullYear()}
          selectedMonth={displayedMonth}
          onSelect={(next) => {
            navigateToMonth(next)
            if (variant === "day") {
              setView("day")
            } else {
              onMonthSelect?.(next)
            }
          }}
          onHeaderClick={() => {
            setYearRangeStart(yearRangeStartFor(displayedMonth))
            setView("year")
          }}
        />
      )}
      {view === "year" && (
        <YearGrid
          rangeStart={yearRangeStart}
          selectedYear={displayedMonth.getFullYear()}
          onSelect={(year) => {
            navigateToMonth(setYear(displayedMonth, year))
            if (variant === "year") {
              onYearSelect?.(year)
            } else {
              setView("month")
            }
          }}
          onNavigate={setYearRangeStart}
        />
      )}
      {view === "day" && (
        <DayPicker
          month={displayedMonth}
          onMonthChange={navigateToMonth}
          showOutsideDays={showOutsideDays}
          locale={de}
          weekStartsOn={1}
          className={cn("w-fit")}
          classNames={{
            months: "flex flex-col gap-2",
            month: "flex flex-col gap-1",
            caption: "flex items-center justify-center",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell:
              "h-8 w-9 text-center text-xs font-normal text-labels-secondary",
            row: "flex w-full justify-between",
            cell: "relative p-0 text-center",
            day: "flex size-9 items-center justify-center rounded-full text-sm text-brand-blue-100 hover:bg-neutrals-v2-200 focus-visible:outline-none",
            day_today: "bg-neutrals-v2-300",
            // `!` nötig: Ist ein Tag zugleich "heute" und "ausgewählt", müssen die
            // rote Auswahl-Füllung und der weiße Text die graue Heute-Markierung überstimmen.
            day_selected:
              "bg-brand-red-200! font-semibold text-neutrals-v2-100! hover:bg-brand-red-200!",
            day_outside: "text-neutrals-v2-700",
            // Figma-State „disabled": nur `labels/secondary` (bereits 60 % Alpha), keine zusätzliche Opacity.
            day_disabled: "text-labels-secondary hover:bg-transparent",
            day_hidden: "invisible",
            ...classNames,
          }}
          components={{
            Caption: () => (
              <DayCaption
                onLabelClick={() => setView("month")}
                showYearNavigation={showYearNavigation}
              />
            ),
          }}
          {...props}
        />
      )}
      {showTodayButton && (
        <Button
          type="button"
          variant="ghost"
          size="small"
          onClick={() => {
            const today = new Date()
            navigateToMonth(today)
            setYearRangeStart(yearRangeStartFor(today))
          }}
        >
          {todayLabel}
        </Button>
      )}
    </div>
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
